import React from "react";
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';

const hexToRgba = (hex: string, alpha: number = 1): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

interface Props {
    className?: string;
    title?: string;
    count?: string;
    color: string;
    onPress?: () => void;
    icon?: React.ReactNode;
}

const StyledCounterCard = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'bgColor'
})<{ bgColor: string }>(({ bgColor }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '14px 16px',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: hexToRgba(bgColor, 0.12),
    border: 'none',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
    minWidth: 160,
    minHeight: 80,
    position: 'relative',
    
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    }
}));

const LeftColumn = styled(Box)({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12
});

const IconCircle = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'bgColor'
})<{ bgColor: string }>(({ bgColor }) => ({
    width: 40,
    height: 40,
    borderRadius: '8px',
    backgroundColor: hexToRgba(bgColor, 0.18),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {
        width: 24,
        height: 24,
        color: bgColor
    }
}));

const CountText = styled(Box)<{ textColor: string }>(({ textColor }) => ({
    fontSize: 32,
    fontWeight: 700,
    color: textColor,
    lineHeight: 1,
    position: 'absolute',
    right: '16px',
    top: '50%',
    transform: 'translateY(-50%)'
}));

const LabelText = styled('span')(({ theme }) => ({
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    textAlign: 'left'
}));

const ScooterCounItem = ({ className, title, count, color, onPress, icon }: Props) => {
    return (
        <StyledCounterCard 
            className={className}
            bgColor={color}
            onClick={() => onPress?.()}
        >
            <LeftColumn>
                <IconCircle bgColor={color}>
                    {icon || (
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                    )}
                </IconCircle>
                <LabelText>{title}</LabelText>
            </LeftColumn>
            <CountText textColor={color}>{count}</CountText>
        </StyledCounterCard>
    )
}

export default ScooterCounItem;
